import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, ListItemText, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, makeStyles, useTheme
} from '@material-ui/core'
import { useServices, useTranslation, components } from 'cng-web-lib'
import { useForm, FormProvider } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import UaeMpciKeys from 'src/constants/locale/key/UaeMpci'
import Namespace from 'src/constants/locale/Namespace'
import pathMap from 'src/paths/pathMap'
import ConfirmDialog from 'src/views/common/ConfirmDialog'
import TruncCell from '../Common/components/TruncCell'
import PartyMasterService from './PartyMasterService'
import PartyFormProperties from './FormProperties'
import MpciApiClient from '../Common/services/MpciApiClient'
import MpciScrollBar from '../Common/components/MpciScrollBar'
import ClearableCngSelectField from '../Common/components/ClearableCngSelectField'
import OrgActingSelector from '../Common/components/OrgActingSelector'
import { COUNTRY_OPTIONS } from '../Common/constants/country'

import { PARTY_TYPE, PARTY_ID_TYPE } from '../Common/constants/entityOptions'
import useOrgFilter from '../Common/hooks/useOrgFilter'

const {
  button: { CngButton },
  form: { field: { CngTextField, CngSelectField } },
  CngGridItem
} = components

const useStyles = makeStyles((theme) => ({
  tableHead: {
    backgroundColor: theme.palette.background.sectionOddBg,
    '& .MuiTableCell-head': {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      padding: '10px 12px',
      whiteSpace: 'nowrap'
    }
  },
  tableRow: {
    '& .MuiTableCell-root': {
      padding: '8px 12px',
      fontSize: theme.typography.body2.fontSize,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  },
  filterCell: {
    padding: '4px 8px !important'
  },
  tableWrapper: {
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  bottomScrollBar: {
    position: 'sticky',
    bottom: 0
  },
}))

// Label helpers
const PARTY_TYPE_LABEL = Object.fromEntries(PARTY_TYPE.map(o => [o.value, o.text.split(' - ').slice(1).join(' - ')]))
const PARTY_ID_TYPE_LABEL = Object.fromEntries(PARTY_ID_TYPE.map(o => [o.value, o.text.split(' - ').slice(1).join(' - ')]))
const COUNTRY_LABEL = Object.fromEntries(COUNTRY_OPTIONS.map(o => [o.value, o.text]))

function StatCard({ icon, iconBg, iconColor, label, value, loading }) {
  const classes = useStyles()
    return (
    <Paper variant='outlined' style={{ padding: 16, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
      <Box style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, background: iconBg }}>
        <FontAwesomeIcon icon={['fal', icon]} style={{ color: iconColor }} />
      </Box>
      <Box>
        <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
          {label}
        </Typography>
        <Typography variant='h5' style={{ fontWeight: 700, lineHeight: 1.2 }}>
          {loading ? '—' : value ?? 0}
        </Typography>
      </Box>
    </Paper>
  )
}

function TablePage(props) {
  const { history, showNotification } = props
  const classes = useStyles()
  const theme = useTheme()
  const { securedSendRequest } = useServices()
  const { translate } = useTranslation([Namespace.UI_COMPONENT, Namespace.UAE_MPCI])

  // ── Table state ──────────────────────────────────────────────────────────
  const [rows, setRows] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState({ field: 'createdDate', direction: 'DESC' })
  const [filters, setFilters] = useState({ id: '', orgId: [], partyName: '', partyType: '', partyIdentificationType: '', city: '', country: '' })


  // ── Stats state ──────────────────────────────────────────────────────────
  const [stats, setStats] = useState({ totalPartiesCount: 0, partyTypesCount: 0, countriesCount: 0, updatedThisWeekCount: 0 })

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({ open: false, party: null })
  const [cloneDialog, setCloneDialog] = useState({ open: false, party: null })
  const [cloneSaving, setCloneSaving] = useState(false)
  const cloneMethods = useForm({ defaultValues: PartyFormProperties.formikProps.initialValues })

    const deleteWarningMessage = translate(Namespace.UAE_MPCI, UaeMpciKeys.DELETE_WARNING_MESSAGE)
  const okDeleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.OK_DELETE_BUTTON)
  const cancelDeleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.CANCEL_DELETE_BUTTON)
  const deleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.DELETE_BUTTON)

    // ── Org filter — column multiselect (Organisation) is the source of truth, same as
  // Master/House Bill; no separate toolbar dropdown here.
  const { isAdmin, canActAsOtherOrg, orgList, defaultOrgIds, orgFilterLoading } = useOrgFilter()
  const orgOptions = orgList.map((o) => ({ value: o.id, label: o.name }))
  const allowedOrgIds = orgList.map((o) => o.id)

  // ── Fetch ────────────────────────────────────────────────────────────────
  const debounceRef = useRef(null)
  const tableContentRef = useRef(null)
  const topScrollBarRef = useRef(null)
  const bottomScrollBarRef = useRef(null)
  const tableContentWrapperRef = useRef(null)

    const fetchData = useCallback((p = page, ps = pageSize, s = sort, f = filters) => {
    setLoading(true)
    // Column multiselect is the source of truth. Clamp against what this user is actually
    // allowed to see, and default to own+child+grandchild orgs when nothing picked — same
    // resolution MpciTable uses for Master/House Bill's orgId column filter.
    const picked = Array.isArray(f.orgId) && f.orgId.length > 0 ? f.orgId : null
    const clampedPicked = picked && allowedOrgIds.length > 0
      ? picked.filter((id) => allowedOrgIds.includes(id))
      : picked
    const resolvedOrgId = isAdmin
      ? (clampedPicked && clampedPicked.length > 0 ? clampedPicked : null)
      : (clampedPicked && clampedPicked.length > 0 ? clampedPicked : defaultOrgIds)
    const body = {
      page: p, size: ps,
      sortBy: s.field, sortDir: s.direction,
      ...(resolvedOrgId != null ? { orgId: resolvedOrgId } : {}),
      id: f.id || null,
      partyName: f.partyName || null,
      partyType: f.partyType || null,
      partyIdentificationType: f.partyIdentificationType || null,
      city: f.city || null,
      country: f.country || null

    }
    MpciApiClient.post(
      UaeMpciApiUrls.PARTY_MASTER_LIST,
      body,
      (data) => {
        setRows(data?.records?.content ?? [])
        setTotalElements(data?.records?.totalElements ?? 0)
        setTotalPages(data?.records?.totalPages ?? 0)
        setStats({
          totalPartiesCount: data?.totalPartiesCount ?? 0,
          partyTypesCount: data?.partyTypesCount ?? 0,
          countriesCount: data?.countriesCount ?? 0,
          updatedThisWeekCount: data?.updatedThisWeekCount ?? 0
        })
        setLoading(false)
      },
      () => { setLoading(false) }
    )
  }, [page, pageSize, sort, filters, allowedOrgIds, isAdmin, defaultOrgIds]) // eslint-disable-line react-hooks/exhaustive-deps

  // Skip while org scope is still resolving — avoids firing against the narrow placeholder
  // defaultOrgIds and then again once the real (possibly wider) scope resolves. The org-scope
  // effect below fires the single initial fetch once orgFilterLoading flips false.
  useEffect(() => {
    if (orgFilterLoading) return
    fetchData(page, pageSize, sort, filters)
  }, [page, pageSize, sort]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orgFilterLoading) return
    setPage(0)
    fetchData(0, pageSize, sort, filters)
  }, [orgFilterLoading, defaultOrgIds?.length]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleFilterChange(name, value) {
    const next = { ...filters, [name]: value }
    setFilters(next)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { setPage(0); fetchData(0, pageSize, sort, next) }, 500)
  }

  function handleSort(field) {
    const next = { field, direction: sort.field === field && sort.direction === 'ASC' ? 'DESC' : 'ASC' }
    setSort(next)
    setPage(0)
  }

    function openCloneDialog(row) {
    const formValues = PartyFormProperties.toClientDataFormat(row)
    // Clone should default "Filing For" to the logged-in party (OrgActingSelector's own
    // auto-default effect fills this in), not the source record's org.
    cloneMethods.reset({ ...formValues, id: null, onBehalfOfOrgId: null })
    setCloneDialog({ open: true, party: row })
  }

  function handleCloneSave() {
    cloneMethods.handleSubmit((formData) => {
      setCloneSaving(true)
      const payload = PartyFormProperties.toServerDataFormat({ ...formData, id: null })
      PartyMasterService.create(
        payload,
        securedSendRequest,
        () => {
          setCloneSaving(false)
                setCloneDialog({ open: false, party: null })
          showNotification('success', 'Party cloned successfully.')
          fetchData()
        },
        () => {
          setCloneSaving(false)
          showNotification('error', 'Failed to save cloned party. Please try again.')
        }
      )
    })()
  }

    function handleDeleteParty() {
    if (confirmDialog.party) {
      MpciApiClient.del(
        `${UaeMpciApiUrls.PARTY_MASTER_DELETE}/${confirmDialog.party.id}`,
        {},
        () => {
          showNotification('success', 'Delete succeeded')
          setConfirmDialog({ open: false, party: null })
          fetchData()
        },
        (error) => {
          console.log(error)
          showNotification('error', 'Delete failed')
        }
      )
    }
  }

            const COLS = [
    { field: 'id',                      label: 'Party Master System ID' },
    { field: 'orgId',                   label: 'Organisation Name' },
    { field: 'partyName',               label: 'Party Name' },
    { field: 'partyType',               label: 'Party Type' },
    { field: 'partyIdentificationType', label: 'Identification Type' },
    { field: 'city',                    label: 'City' },
    { field: 'country',                 label: 'Country' },
    { field: '_actions',                label: 'Actions' }
  ]
  const start = totalElements === 0 ? 0 : page * pageSize + 1
  const end = Math.min(page * pageSize + pageSize, totalElements)

  

      return (
    <Box>
      {/* stat cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {[
          { icon: 'users', iconBg: '#ede9fe', iconColor: '#7c3aed', label: 'Total Parties',     key: 'totalPartiesCount' },
          { icon: 'tag',   iconBg: '#dbeafe', iconColor: '#2563eb', label: 'Party Types',       key: 'partyTypesCount' },
          { icon: 'flag',  iconBg: '#d1fae5', iconColor: '#059669', label: 'Countries',         key: 'countriesCount' },
          { icon: 'clock', iconBg: '#fef3c7', iconColor: '#d97706', label: 'Updated This Week', key: 'updatedThisWeekCount' }
        ].map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.key}>
            <StatCard icon={s.icon} iconBg={s.iconBg} iconColor={s.iconColor} label={s.label} value={stats[s.key]} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Paper variant='outlined'>
        {/* toolbar */}
        <Box px={2} py={1.5} display='flex' alignItems='center' justifyContent='space-between'
          style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant='caption' color='textSecondary'>
            {totalElements > 0 ? `Showing ${start} to ${end} of ${totalElements} entries` : ''}
          </Typography>
                    <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Tooltip title='Refresh'>
              <IconButton size='small' onClick={() => fetchData()}>
                <FontAwesomeIcon icon={['fal', 'refresh']} />
              </IconButton>
            </Tooltip>
            <Button color='primary' variant='contained' size='small'
              startIcon={<FontAwesomeIcon icon={['fal', 'plus-circle']} />}
              onClick={() => history.push(pathMap.UAE_MPCI_PARTY_MASTER_ADD_VIEW)}
            >Add Party</Button>
          </Box>
        </Box>

        {/* table */}
        <Box position='relative'>
          <MpciScrollBar
            tableContentRef={tableContentRef}
            ref={topScrollBarRef}
            onScroll={(e) => {
              if (bottomScrollBarRef.current) bottomScrollBarRef.current.scrollLeft = e.target.scrollLeft
              if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
            }}
          />
          <div className={classes.tableWrapper} ref={tableContentWrapperRef}>
            <Table size='small' ref={tableContentRef}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {COLS.map((col) => (
                                        <TableCell key={col.field} align={col.field === '_actions' ? 'center' : 'left'}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.id}
                      onChange={(e) => handleFilterChange('id', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <Select
                      multiple
                      displayEmpty
                      value={filters.orgId}
                      onChange={(e) => handleFilterChange('orgId', e.target.value)}
                      size='small'
                      style={{ fontSize: 12, minWidth: 130, maxWidth: 220 }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return <em style={{ color: '#aaa', fontSize: 12 }}>All</em>
                        const labels = selected.map((v) => orgOptions.find((o) => o.value === v)?.label ?? v)
                        return labels.length <= 2
                          ? <span style={{ fontSize: 12 }}>{labels.join(', ')}</span>
                          : <span style={{ fontSize: 12 }}>{labels[0]}, +{labels.length - 1} more</span>
                      }}
                    >
                      {orgOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} style={{ fontSize: 12, padding: '4px 8px' }}>
                          <Checkbox checked={filters.orgId.indexOf(opt.value) > -1} size='small' style={{ padding: 4 }} />
                          <ListItemText primary={opt.label} primaryTypographyProps={{ style: { fontSize: 12 } }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.partyName}
                      onChange={(e) => handleFilterChange('partyName', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <Select value={filters.partyType} displayEmpty size='small' style={{ fontSize: 12, minWidth: 100 }}
                      onChange={(e) => handleFilterChange('partyType', e.target.value)}>
                      <MenuItem value=''>All</MenuItem>
                      {PARTY_TYPE.map((o) => <MenuItem key={o.value} value={o.value}>{o.text}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <Select value={filters.partyIdentificationType} displayEmpty size='small' style={{ fontSize: 12, minWidth: 80 }}
                      onChange={(e) => handleFilterChange('partyIdentificationType', e.target.value)}>
                      <MenuItem value=''>All</MenuItem>
                      {PARTY_ID_TYPE.map((o) => <MenuItem key={o.value} value={o.value}>{o.text}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.city}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <Select value={filters.country} displayEmpty size='small' style={{ fontSize: 12, minWidth: 120 }}
                      onChange={(e) => handleFilterChange('country', e.target.value)}>
                      <MenuItem value=''>All</MenuItem>
                      {COUNTRY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.text}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow><TableCell colSpan={COLS.length} align='center' style={{ padding: 40 }}>
                    <FontAwesomeIcon icon={['fal', 'inbox']} style={{ fontSize: 32, color: theme.palette.text.disabled, marginBottom: 8 }} />
                    <Typography variant='body2' color='textSecondary'>No records found</Typography>
                  </TableCell></TableRow>
                ) : rows.map((row, idx) => (
                  <TableRow key={row.id ?? idx} className={classes.tableRow}
                    onClick={() => history.push(`${pathMap.UAE_MPCI_PARTY_MASTER}/view/${row.id}`)}>
                    <TableCell>
                      <TruncCell value={row.id} />
                    </TableCell>
                    <TableCell>{orgList.find((o) => o.id === row.orgId)?.name ?? '—'}</TableCell>
                    <TableCell><strong>{row.partyName || '—'}</strong></TableCell>
                    <TableCell>{PARTY_TYPE_LABEL[row.partyType] || row.partyType || '—'}</TableCell>
                    <TableCell>{PARTY_ID_TYPE_LABEL[row.partyIdentificationType] || row.partyIdentificationType || '—'}</TableCell>
                    <TableCell>{row.city || '—'}</TableCell>
                    <TableCell>{COUNTRY_LABEL[row.country] || row.country || '—'}</TableCell>
                    <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                      <Box display='flex' alignItems='center' justifyContent='center' style={{ gap: 4 }}>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'eye']} style={{ fontSize: 10 }} />}
                          onClick={() => history.push(`${pathMap.UAE_MPCI_PARTY_MASTER}/view/${row.id}`)}>View</Button>
                        <Button size='small' variant='contained' color='primary' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'pen']} style={{ fontSize: 10 }} />}
                          onClick={() => history.push(`${pathMap.UAE_MPCI_PARTY_MASTER}/edit/${row.id}`)}>Edit</Button>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'copy']} style={{ fontSize: 10 }} />}
                          onClick={() => openCloneDialog(row)}>Copy as Draft</Button>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap', color: theme.palette.error.main, borderColor: theme.palette.error.main }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'trash']} style={{ fontSize: 10 }} />}
                          onClick={() => setConfirmDialog({ open: true, party: row })}>Delete</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <MpciScrollBar
            className={classes.bottomScrollBar}
            tableContentRef={tableContentRef}
            ref={bottomScrollBarRef}
            onScroll={(e) => {
              if (topScrollBarRef.current) topScrollBarRef.current.scrollLeft = e.target.scrollLeft
              if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
            }}
          />
        </Box>
        {/* pagination */}
        <Box px={2} py={1} display='flex' alignItems='center' justifyContent='flex-end'
          style={{ gap: 8, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant='body2'>Rows:</Typography>
          <Select value={pageSize} size='small' onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}>
            {[10, 20, 50].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
          <Typography variant='body2'>Page {totalPages === 0 ? 0 : page + 1} of {totalPages}</Typography>
          <IconButton size='small' onClick={() => setPage(0)} disabled={page === 0}><FontAwesomeIcon icon={['fal', 'angles-left']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(page - 1)} disabled={page === 0}><FontAwesomeIcon icon={['fal', 'chevron-left']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><FontAwesomeIcon icon={['fal', 'chevron-right']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><FontAwesomeIcon icon={['fal', 'angles-right']} /></IconButton>
        </Box>
      </Paper>
            <ConfirmDialog
        isConfirmDialogOpen={confirmDialog.open}
        closeDialog={() => setConfirmDialog({ open: false, party: null })}
        confirmDialog={handleDeleteParty}
        content={deleteWarningMessage}
        okMsg={okDeleteButton}
        cancelMsg={cancelDeleteButton}
        title={deleteButton}
      />

      {/* ── Clone Party Dialog ── */}
      <Dialog
        open={cloneDialog.open}
        onClose={() => !cloneSaving && setCloneDialog({ open: false, party: null })}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' style={{ gap: 10 }}>
              <FontAwesomeIcon icon={['fal', 'copy']} />
              <Typography variant='h6'>Copy Party as New Draft</Typography>
            </Box>
            <IconButton size='small' onClick={() => setCloneDialog({ open: false, party: null })} disabled={cloneSaving}>
              <FontAwesomeIcon icon={['fal', 'times']} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
                              <Box mb={2} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '10px 14px', background: theme.palette.primary[50], border: `1px solid ${theme.palette.primary[200]}`, borderRadius: 8 }}>
            <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 14, marginTop: 2, flexShrink: 0, color: theme.palette.primary.dark }} />
            <Typography variant='caption' style={{ color: theme.palette.primary.dark }}>
              Clone of an existing party — Edit the details before saving.
            </Typography>
          </Box>

            <FormProvider {...cloneMethods}>
              <Grid container spacing={2}>
                {canActAsOtherOrg && (
                  <CngGridItem xs={12} sm={6}>
                    <OrgActingSelector name='onBehalfOfOrgId' disabled={cloneSaving} />
                  </CngGridItem>
                )}
                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField required name='partyType' label='Party Type *' size='small' items={PARTY_TYPE} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField required name='partyIdentificationType' label='Party Identification Type *' size='small' items={PARTY_ID_TYPE} />
                </CngGridItem>
                <CngGridItem xs={12}>
                  <CngTextField required name='partyName' label='Party Name *' size='small' inputProps={{ maxLength: 70 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CngTextField required name='address1' label='Address 1 *' size='small' inputProps={{ maxLength: 256 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CngTextField name='address2' label='Address 2' size='small' inputProps={{ maxLength: 256 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CngTextField name='address3' label='Address 3' size='small' inputProps={{ maxLength: 256 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CngTextField name='city' label='City' size='small' inputProps={{ maxLength: 35 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField name='country' label='Country' size='small' items={COUNTRY_OPTIONS} />
                </CngGridItem>
              </Grid>
            </FormProvider>
        </DialogContent>

        <DialogActions>
          <CngButton variant='outlined' onClick={() => setCloneDialog({ open: false, party: null })} disabled={cloneSaving}>
            Cancel
          </CngButton>
          <CngButton
            color='primary'
            onClick={handleCloneSave}
            disabled={cloneSaving}
            shouldShowProgress={cloneSaving}
            startIcon={<FontAwesomeIcon icon={['fal', 'save']} />}
          >
            Save Party
          </CngButton>
        </DialogActions>
            </Dialog>
    </Box>
  )
}

export default TablePage
